/**
 * KPI tiles in the dashboard artboard pattern: serif figure, delta, sparkline.
 * Grid is 4 / 2 / 1 columns (plan 2 §11.2.1).
 */
import { el } from '../../core/dom.js';
import { figureText, isUnverified, UNVERIFIED_TITLE } from './kpi-figure.js';
import { resolvedOf } from './shape.js';

function sparkPath(values) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = values.length === 1 ? 0 : 200 / (values.length - 1);
  return values.map((value, index) => {
    const x = (index * step).toFixed(1);
    const y = (34 - ((value - min) / span) * 28).toFixed(1);
    return `${index === 0 ? 'M' : 'L'}${x} ${y}`;
  }).join(' ');
}

function sparkline(points) {
  const values = (points || []).map(Number).filter((num) => Number.isFinite(num));
  if (values.length < 2) return null;
  const line = sparkPath(values);
  return el('svg', {
    class: 'chat-spark',
    viewBox: '0 0 200 40',
    width: '100%',
    height: '40',
    preserveAspectRatio: 'none',
    'aria-hidden': 'true',
  },
  el('path', { d: `${line} L200 40 L0 40 Z`, fill: 'var(--signal-tint)' }),
  el('path', { d: line, fill: 'none', stroke: 'var(--signal)', 'stroke-width': '1.75' }));
}

function toneOf(item, key) {
  const entry = item && item[key];
  if (!entry || typeof entry !== 'object') return 'neutral';
  return entry.tone || 'neutral';
}

export { figureText } from './kpi-figure.js';

function markText(text, unverified) {
  if (!unverified) return text;
  return el('span', { class: 'chat-unverified', title: UNVERIFIED_TITLE }, text);
}

function figureTone(item) {
  // S46b — colour the big number from item.tone (or delta tone fallback).
  if (item && item.tone) return String(item.tone);
  return toneOf(item, 'delta');
}

function tile(item) {
  const delta = item.delta && typeof item.delta === 'object' ? item.delta.text : item.delta;
  const badge = item.badge
    || (item.expected && typeof item.expected === 'object' ? item.expected.label : '')
    || '';
  const trend = item.trend || item.sparkline;
  const figureUnverified = isUnverified(item);
  const deltaUnverified = item.delta && typeof item.delta === 'object' && isUnverified(item.delta);
  const tone = figureTone(item);
  const figureClass = figureUnverified ? 'chat-figure is-unverified' : `chat-figure is-${tone}`;
  return el('article', { class: 'chat-kpi', 'aria-label': item.label || 'Figure' },
    el('p', { class: 'chat-kicker' }, item.label || ''),
    el('p', { class: figureClass }, markText(figureText(item), figureUnverified)),
    delta ? el('p', { class: `chat-delta is-${toneOf(item, 'delta')}${deltaUnverified ? ' is-unverified' : ''}` },
      markText(String(delta), deltaUnverified)) : null,
    badge ? el('p', { class: `chat-badge is-${toneOf(item, 'expected')}`, role: 'status' }, String(badge)) : null,
    item.caption ? el('p', { class: 'chat-caption' }, String(item.caption)) : null,
    sparkline(trend));
}

/**
 * @param {object} block
 * @returns {HTMLElement}
 */
export function renderKpi(block) {
  const data = resolvedOf(block);
  const items = Array.isArray(data.items) ? data.items.slice(0, 8) : [];
  const grid = el('div', { class: 'chat-kpi-grid' }, items.map(tile));
  if (!items.length) grid.append(el('p', { class: 'chat-note' }, 'No figures in this block'));
  return el('section', {
    class: 'chat-block chat-block-kpi',
    'data-block-type': 'kpi',
    'data-block-id': (block && block.id) || '',
    'aria-label': 'Key figures',
  }, grid);
}
